import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { MinimalButton } from '@ovasabi/ui-minimal'
import { FilterPills, PageHeader, Screen, SearchField, StatusDot, type PillOption, type ChowStatus } from '../../components/chow'
import {
  ExploreCard,
  ExploreEmpty,
  ExploreGrid,
  ExploreState,
} from '../../components/chow/ExploreCard'
import { ChefHatIcon, FlameIcon } from '../../components/icons'
import {
  useDiscoverChefSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { allowSeedContent, isLiveRuntime } from '../../lib/runtime'
import { isVendorOpen } from '../../lib/vendorSchedule'
import { usePageContext } from './ChooseChowContext'
import { chefs, type Chef } from './content'
import { useHotChefs } from './trending'
import { ExploreNavigation } from './ExploreNavigation'
import { ChowThumb } from './ChowThumb'
import { chefPhoto } from './dishImages'
import { CHEF_CATEGORY_BY_TYPE, enumTail, isSellingKitchen, useChefVerification, useChefDirectory, useUiDishes, type ChefEntry } from './projections'
import { usePublicChefs, type PublicChef } from '../../lib/discover'
import { useDeliveryLocation } from '../../lib/deliveryLocation'
import { distanceNote, useCities } from '../../lib/places'
import { DeliveryLocationModal } from './DeliveryLocationModal'
import { canonicalSlug, slugify } from '../../lib/slug'
import { formatRatingMeta } from '../../lib/formatters'
import { displayTitle } from '../../lib/displayName'

type FilterKey = 'all' | 'Home Chef' | 'Food Truck' | 'Bakery' | 'Caterer' | 'Drinks'

/**
 * How far to look, in city-to-city kilometres. "near" is the customer's city
 * and its satellites (Kubwa, Nyanya, Lugbe are 10–20 km from central Abuja);
 * "region" reaches the next towns over. A kitchen whose city is not known yet
 * only shows under "anywhere".
 */
type DistanceKey = 'near' | 'region' | 'anywhere'
const DISTANCE_LIMIT_KM: Record<DistanceKey, number> = { near: 30, region: 100, anywhere: Infinity }

/** Only the trending badge is chef-specific; the card itself is shared. */
const ChefStyle = {
  ChangeCity: styled(MinimalButton)`
    align-self: flex-start;
    padding: 0;
    border: none;
    background: none;
    color: ${v.color.brand};
    font-size: ${v.typography.captionSize};
    font-weight: 700;
    cursor: pointer;
  `,
  Overlay: styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
  `,
  Hot: styled.span`
    position: absolute;
    top: 8px;
    left: 8px;
    display: inline-grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: ${v.radius.pill};
    color: ${v.color.warning};
    background: ${v.color.bgSurface};
    box-shadow: ${v.shadow.subtle};
  `,
  FooterStatus: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: ${v.typography.captionSize};
    color: ${v.color.textSecondary};
  `,
  StatusText: styled.span`
    font-weight: ${v.typography.weightSemibold};
  `,
  PriceFrom: styled.span`
    color: ${v.color.textSecondary};
    font-size: ${v.typography.captionSize};
    strong {
      color: ${v.color.brand};
      font-weight: ${v.typography.weightBold};
    }
  `,
  Sep: styled.span`
    color: ${v.color.borderStrong};
  `,
}

const options: readonly PillOption<FilterKey>[] = [
  { value: 'all', label: 'All' },
  { value: 'Home Chef', label: 'Home Chef' },
  { value: 'Food Truck', label: 'Food Truck' },
  { value: 'Bakery', label: 'Bakery' },
  { value: 'Caterer', label: 'Caterer' },
  { value: 'Drinks', label: 'Drinks & Coolers' },
]

/** Runtime-shaped chef row the page renders, regardless of read lane. */
interface ChefView extends Chef {
  id: string
  hot: boolean
  reviewCount?: number
  /** City-to-city km from the customer's city; undefined when not known. */
  distanceKm?: number
}

const GLYPH_BY_TYPE: Record<string, string> = {
  home_chef: '👩🏾‍🍳',
  food_truck: '🚚',
  bakery: '🧁',
  caterer: '🍲',
  drinks: '🍹',
  drinks_and_coolers: '🍹',
}

/**
 * The prototype runtime seeds projection stores with placeholder rows whose
 * string fields are literal field tokens (`display_name-1`, `cuisine-3`). In
 * dev the runtime reports `live` (a projection source resolves from the page
 * origin) even though no real backend has populated the store, so those tokens
 * would otherwise render verbatim. Treat any `<field>-<n>` token as absent so we
 * fall back to curated copy; a real projection value ("Amara Okafor") is kept.
 */
const PLACEHOLDER_TOKEN = /^[a-z][a-z_]*-\d+$/
const isRealValue = (value: unknown): value is string => {
  const str = typeof value === 'string' ? value.trim() : ''
  return Boolean(str) && !PLACEHOLDER_TOKEN.test(str)
}
const realOr = (value: unknown, fallback: string): string =>
  isRealValue(value) ? value.trim() : fallback

/**
 * Resolve a live vendor projection record onto the Chef shape the row expects,
 * falling back per-field to a seeded chef so a partially-populated (or
 * placeholder-seeded) record still reads cleanly.
 */
const mapVendorRecord = (
  record: Record<string, unknown>,
  index: number,
  directory: Map<string, ChefEntry>,
): Chef => {
  const fallback = chefs[index % chefs.length]
  const type = enumTail(record.vendor_type)
  const entry = directory.get(String(record.profile_id ?? ''))
  const cuisine = realOr(record.cuisine, fallback.cuisine)
  const name = displayTitle(realOr(record.display_name, fallback.name))
  return {
    name,
    glyph: GLYPH_BY_TYPE[type] || fallback.glyph,
    image: chefPhoto(String(record.avatar_image_url ?? '')) ?? chefPhoto(entry?.avatar, cuisine, name),
    category: CHEF_CATEGORY_BY_TYPE[type] || fallback.category,
    cuisine,
    area: realOr(record.location_label, fallback.area),
    tags: fallback.tags,
    rating: 0,
    subs: 40 + (index % 10) * 15,
    priceFrom: 0,
    status: (isVendorOpen(record) ? 'open' : 'closed') as ChowStatus,
    badge: String(record.badge_tier ?? 'none'),
  }
}

/** The last part of a typed area: "Abuja" from "Wuse II, Abuja". */
const typedCity = (area: string): string => area.split(',').map((p) => p.trim()).filter(Boolean).pop() || 'you'

export const ChefsPage = () => {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<FilterKey>('all')
  const [distance, setDistance] = useState<DistanceKey>('anywhere')
  const [locationOpen, setLocationOpen] = useState(false)
  const location = useDeliveryLocation()
  const { byRef } = useCities()
  const myCity = byRef.get(location.cityRef)?.name ?? typedCity(location.area)
  const [query, setQuery] = useState('')
  const page = usePageContext('/kitchens')
  const vendors = useVendorProfileSnapshot() as { records: readonly Record<string, unknown>[] }
  const discoverSnap = useDiscoverChefSnapshot() as { records?: readonly Record<string, unknown>[] }
  const directory = useChefDirectory()
  const verification = useChefVerification()
  const hot = useHotChefs(3)
  // The public feed refines ordering when it lands; the rows themselves come from
  // the projection, which is synchronous. So this page is not "loading" while the
  // fetch is in flight — gating on that would replace a list the page already has
  // with placeholders on every cold start and every background refresh.
  // The customer's city orders the feed nearest first and gives each kitchen a
  // distance; it never hides one on its own (the distance pills do that).
  const { chefs: publicChefs } = usePublicChefs(50, location.cityRef || location.area)
  const { records: dishes } = useUiDishes()

  // Real minimum price per chef calculated from active catalog dishes
  const minPriceMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const d of dishes) {
      if (d.status === 'hidden' || !d.price || d.price <= 0) continue
      const chefName = d.chef.trim().toLowerCase()
      const chefId = d.chefId ? d.chefId.toLowerCase() : ''
      if (chefName) {
        const cur = map.get(chefName) ?? Infinity
        map.set(chefName, Math.min(cur, d.price))
      }
      if (chefId) {
        const cur = map.get(chefId) ?? Infinity
        map.set(chefId, Math.min(cur, d.price))
      }
    }
    return map
  }, [dishes])

  // Reads follow the live-seam pattern (mirrors `trending.ts`). The prototype
  // runtime seeds every store with placeholder rows whose fields are literal
  // field tokens ("display_name-1"), and in dev it still reports `live` (a
  // projection source resolves from the page origin). So we only treat the
  // vendor snapshot as real data when it carries at least one non-placeholder
  // record; otherwise we render the curated catalog. Either way the row shape is
  // identical, so the rest of the page never branches.
  const hotNames = useMemo(() => new Set(hot.records.map((c) => c.name)), [hot.records])

  const visible = useMemo<readonly ChefView[]>(() => {
    // 1. Build live open/status map from discoverSnap (realtime Hermes stream)
    const liveOpenMap = new Map<string, boolean>()
    for (const r of discoverSnap.records || []) {
      const isOpen = Boolean(r.open)
      if (r.id) liveOpenMap.set(String(r.id).toLowerCase(), isOpen)
      if (r.profile_id) liveOpenMap.set(String(r.profile_id).toLowerCase(), isOpen)
      if (r.vendor_id) liveOpenMap.set(String(r.vendor_id).toLowerCase(), isOpen)
      if (r.name) liveOpenMap.set(String(r.name).trim().toLowerCase(), isOpen)
    }

    // 2. Discover chefs snapshot (sanitized, validated, and deduplicated)
    const liveDiscoverChefs: PublicChef[] = (discoverSnap.records || [])
      .filter((c) => {
        const name = String(c.name || '').trim()
        const id = String(c.id || '')
        return (
          isRealValue(name) &&
          name.toLowerCase() !== 'deleted kitchen' &&
          !name.toLowerCase().includes('deleted') &&
          !id.includes('-prototype') &&
          String(c.badge || '') !== 'revoked'
        )
      })
      .map((c, index) => ({
        id: String(c.id || `disc-chef-${index}`),
        name: String(c.name || 'Kitchen'),
        cuisine: String(c.cuisine || ''),
        area: String(c.area || ''),
        open: Boolean(c.open),
        avatar: String(c.avatar || ''),
        badge: String(c.badge || ''),
        rating: Number(c.rating) || 4.8,
        reviews: Number(c.reviews) || 0,
        orders: Number(c.orders) || 0,
        dish_count: Number(c.dish_count) || 0,
        vendor_type: String(c.vendor_type || 'home_chef'),
      }))

    // Prefer the authoritative SQL publicChefs if available, with liveOpenMap overlay;
    // fallback to sanitized liveDiscoverChefs if publicChefs is empty.
    const effectivePublicChefs = publicChefs.length > 0 ? publicChefs : liveDiscoverChefs

    const realVendorRecords = vendors.records.filter(
      (record) =>
        isRealValue(record.display_name) &&
        record.display_name.trim().toLowerCase() !== 'deleted kitchen' &&
        Boolean(CHEF_CATEGORY_BY_TYPE[enumTail(record.vendor_type)]) &&
        isSellingKitchen(verification.get(String(record.profile_id ?? ''))),
    )

    let base: readonly { chef: Chef; id: string }[] = []

    if (!isLiveRuntime() && realVendorRecords.length > 0) {
      base = realVendorRecords.map((record, index) => ({
        chef: { ...mapVendorRecord(record, index, directory), rating: effectivePublicChefs.find(chef => chef.id === record.id)?.rating || 0 },
        id: String(record.id || index),
      }))
    } else if (effectivePublicChefs.length > 0) {
      base = effectivePublicChefs.map((c, index) => {
        const cat =
          CHEF_CATEGORY_BY_TYPE[enumTail(c.vendor_type)] ||
          (c.cuisine.toLowerCase().includes('drink') || c.cuisine.toLowerCase().includes('zobo')
            ? 'Drinks'
            : c.cuisine.toLowerCase().includes('baker') || c.cuisine.toLowerCase().includes('pastr')
            ? 'Bakery'
            : c.cuisine.toLowerCase().includes('truck') || c.cuisine.toLowerCase().includes('cart')
            ? 'Food Truck'
            : c.cuisine.toLowerCase().includes('cater')
            ? 'Caterer'
            : 'Home Chef')
        const glyph =
          GLYPH_BY_TYPE[enumTail(c.vendor_type)] ||
          (cat === 'Drinks' ? '🍹' : cat === 'Bakery' ? '🧁' : cat === 'Food Truck' ? '🚚' : '👩🏾‍🍳')

        // Overlay realtime open status if available
        const liveOpen =
          liveOpenMap.get(c.id.toLowerCase()) ??
          liveOpenMap.get(c.name.trim().toLowerCase()) ??
          c.open

        // Real minimum price: backend price_from_minor or computed from live active dishes
        const backendPrice = c.price_from_minor && c.price_from_minor > 0
          ? Math.round(c.price_from_minor / 100)
          : 0
        const localPrice =
          minPriceMap.get(c.id.toLowerCase()) ??
          minPriceMap.get(c.name.trim().toLowerCase()) ??
          0
        const priceFrom = backendPrice > 0 ? backendPrice : localPrice

        return {
          chef: {
            name: c.name,
            glyph,
            image: chefPhoto(c.avatar, c.cuisine, c.name),
            category: cat,
            cuisine: c.cuisine || 'Home kitchen',
            area: c.area || 'Nigeria',
            tags: ['Homemade', 'Verified'],
            rating: c.reviews > 0 ? c.rating : 0,
            subs: c.orders > 0 ? c.orders : 40 + (index % 5) * 12,
            priceFrom,
            status: (liveOpen ? 'open' : 'closed') as ChowStatus,
            badge: c.badge || 'verified',
          },
          id: c.id || `public-chef-${index}`,
        }
      })
    } else if (allowSeedContent()) {
      base = chefs.map((chef, index) => ({ chef, id: `chef-${index}` }))
    }

    // STRICT DEDUPLICATION: eliminate duplicate chef cards by name and id
    const seenNames = new Set<string>()
    const deduplicatedBase = base.filter(({ chef }) => {
      const norm = chef.name.trim().toLowerCase()
      if (!norm || norm === 'deleted kitchen' || norm.includes('deleted') || seenNames.has(norm)) {
        return false
      }
      seenNames.add(norm)
      return true
    })

    const search = query.trim().toLowerCase()
    const limitKm = DISTANCE_LIMIT_KM[distance]
    const band = (km: number | undefined) => (km === undefined ? 2 : km <= DISTANCE_LIMIT_KM.near ? 0 : 1)
    return deduplicatedBase
      .map(({ chef, id }) => {
        const row = publicChefs.find((c) => c.id === id)
        return { ...chef, id, hot: hotNames.has(chef.name), reviewCount: row?.reviews, distanceKm: row?.distance_km }
      })
      .filter((chef) => {
        if (filter !== 'all' && chef.category !== filter) return false
        if (limitKm !== Infinity && (chef.distanceKm === undefined || chef.distanceKm > limitKm)) return false
        if (search && !`${chef.name} ${chef.cuisine} ${chef.area}`.toLowerCase().includes(search)) return false
        return true
      })
      // Nearer first, then this week's trending kitchens within a band.
      .sort((a, b) => band(a.distanceKm) - band(b.distanceKm) || Number(b.hot) - Number(a.hot))
  }, [filter, distance, vendors.records, discoverSnap.records, verification, hotNames, directory, publicChefs, minPriceMap, query])

  const { kitchenId } = useParams()
  useEffect(() => {
    if (!kitchenId || visible.length === 0) return
    const clean = kitchenId.trim().toLowerCase()
    const targetTail = clean.length >= 8 ? clean.slice(-8) : clean
    const matched = visible.find((c) => {
      const id = c.id.toLowerCase()
      if (id === clean) return true
      if (canonicalSlug(c.name, c.id).toLowerCase() === clean) return true
      if (slugify(c.name).toLowerCase() === clean) return true
      if (targetTail && (id.startsWith(targetTail) || id.endsWith(targetTail))) return true
      return false
    })
    if (matched) {
      navigate(`/dishes?chef=${encodeURIComponent(matched.name)}`, { replace: true })
    }
  }, [kitchenId, visible, navigate])

  return (
    <Screen
      header={
        <PageHeader title={page.title} accentDot>
          <SearchField placeholder="Find a chef, cuisine or area" value={query} onChange={setQuery} />
        </PageHeader>
      }
    >
      <ExploreNavigation />
      <FilterPills ariaLabel="Filter chefs" value={filter} options={options} onChange={(val) => setFilter(val)} />
      <FilterPills
        ariaLabel="Filter chefs by distance"
        value={distance}
        options={[
          { value: 'near', label: `Near ${myCity}` },
          { value: 'region', label: 'Within 100 km' },
          { value: 'anywhere', label: 'Anywhere' },
        ]}
        onChange={setDistance}
      />
      <ChefStyle.ChangeCity variant="quiet" type="button" onClick={() => setLocationOpen(true)}>
        Change city
      </ChefStyle.ChangeCity>
      <DeliveryLocationModal open={locationOpen} onClose={() => setLocationOpen(false)} location={location} />
      <ExploreState skeletonCount={6}>
        {visible.length === 0 ? (
          <ExploreEmpty
            icon={<ChefHatIcon />}
            title={
              query
                ? 'No chefs match your search'
                : distance !== 'anywhere'
                ? `No kitchens ${distance === 'near' ? `near ${myCity}` : 'within 100 km'} yet`
                : filter === 'all'
                ? 'No chefs yet'
                : `No ${filter.toLowerCase()}s here`
            }
            description={
              query
                ? 'Try another chef name, cuisine or area.'
                : filter === 'all'
                ? 'Home chefs, trucks and bakeries near you are still joining — check back soon.'
                : 'Try a different category to see who else is cooking.'
            }
            action={
              filter !== 'all' || query || distance !== 'anywhere' ? (
                <MinimalButton
                  variant="secondary"
                  onClick={() => {
                    setFilter('all')
                    setDistance('anywhere')
                    setQuery('')
                  }}
                >
                  Show all chefs
                </MinimalButton>
              ) : undefined
            }
          />
        ) : (
          <ExploreGrid>
            {visible.map((chef) => (
              <ExploreCard
                key={chef.id}
                to={`/kitchen/${canonicalSlug(chef.name, chef.id)}`}
                media={<ChowThumb src={chef.image} emoji={chef.glyph} alt={chef.name} isChef thumbnailWidth={384} />}
                mediaOverlay={
                  <ChefStyle.Overlay>
                    {chef.hot && (
                      <ChefStyle.Hot aria-label="Trending this week">
                        <FlameIcon size="sm" />
                      </ChefStyle.Hot>
                    )}
                  </ChefStyle.Overlay>
                }
                title={chef.name}
                subtitle={([chef.cuisine, chef.area].filter(Boolean).join(' · ') || 'Home kitchen') + distanceNote(chef.distanceKm)}
                meta={formatRatingMeta(chef.rating, chef.reviewCount)}
                leading={
                  <ChefStyle.FooterStatus>
                    <StatusDot status={chef.status} />
                    {chef.priceFrom > 0 && (
                      <>
                        <ChefStyle.Sep aria-hidden>·</ChefStyle.Sep>
                        <ChefStyle.PriceFrom>
                          From <strong>₦{chef.priceFrom.toLocaleString('en-NG')}</strong>
                        </ChefStyle.PriceFrom>
                      </>
                    )}
                  </ChefStyle.FooterStatus>
                }
                trailing="View kitchen →"
              />
            ))}
          </ExploreGrid>
        )}
      </ExploreState>
    </Screen>
  )
}
